import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { Box, Typography } from '@mui/material';
import { CuriosityNamespaceType, useCuriosityTranslation } from '@procureai/curiosity-ui-mui';
import {
  formatNumber,
  NumberFormatPreset,
  QuickViewReferenceValue,
  QuickViewWidget,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { CubeChart, CubeChartType } from '~/components/CubeChart';
import { monthsInYearFormatter } from '~/components/CubeChart/CubeChart.common';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { colorFromStatus } from '~/util/conversion';
import { computeChangeRate, getSegments, getStatus, lastYearDateRange, previousYearDateRange } from '~/util/cube';

const formatChange = formatNumber(NumberFormatPreset.PERCENT_DECIMAL);
const formatValueCompact = formatNumber(NumberFormatPreset.INTEGER_COMPACT);
const formatValue = formatNumber(NumberFormatPreset.INTEGER);

export const MaterialOrderedQuantityWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'MaterialOrderedQuantityWidget' });
  const { ct } = useCuriosityTranslation(CuriosityNamespaceType.UNIT);
  const [widgetRef, active] = useScrollIntoView({ hash: 'material-ordered-quantity' });

  const cube = 'MonthlyOrderedCommodity';

  const filters: Filter[] = [
    {
      member: `${cube}.commodity_id`,
      operator: 'equals',
      values: [entityId],
    },
  ];

  const orderedQuantitySeries = useCubeQuery({
    measures: [`${cube}.monthly_quantity`],
    dimensions: [`${cube}.unit_id`],
    timeDimensions: [
      {
        dimension: `${cube}.date`,
        granularity: 'month',
        dateRange: lastYearDateRange,
      },
    ],
    filters,
  });

  const orderedQuantitySeriesPrevious = useCubeQuery({
    measures: [`${cube}.monthly_quantity`],
    timeDimensions: [
      {
        dimension: `${cube}.date`,
        granularity: 'month',
        dateRange: previousYearDateRange,
      },
    ],
    filters,
  });

  const { segments, totalValue: currentYear } = getSegments(orderedQuantitySeries);
  const { totalValue: previousYear } = getSegments(orderedQuantitySeriesPrevious);
  const changeRate = computeChangeRate(currentYear, previousYear);
  const status = getStatus(changeRate);
  const unitId = orderedQuantitySeries.resultSet?.rawData()?.[0]?.[`${cube}.unit_id`] as string;
  const unitText = ct(unitId)?.value ?? '';

  return (
    <QuickViewWidget
      ref={widgetRef}
      active={active}
      loading={orderedQuantitySeriesPrevious.isLoading || orderedQuantitySeries.isLoading}
      title={t('title')}
      subTitle={t('subTitle', { unitText })}
      value={formatValueCompact(currentYear)}
      valueTooltip={formatValue(currentYear)}
      data-intercom-target="MaterialOrderedQuantityWidget"
      referenceValue={
        <QuickViewReferenceValue
          description={formatValueCompact(previousYear)}
          value={formatChange(changeRate)}
          dotColor={colorFromStatus(status)}
        />
      }
    >
      {segments && currentYear ? (
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 0',
            alignItems: 'flex-end',
          }}
        >
          <Box
            sx={{
              flex: '1 1 0',
              display: 'flex',
              mb: '-5px',
              mr: 3,
            }}
          >
            <Typography component="div" variant="caption" color="textPrimary">
              {t('chartCaption')}
            </Typography>
          </Box>
          <CubeChart
            type={CubeChartType.tinyBar}
            formatter={{
              x: monthsInYearFormatter,
              y: formatValueCompact,
            }}
            {...orderedQuantitySeries}
          />
        </Box>
      ) : (
        <PlaceholderWidget />
      )}
    </QuickViewWidget>
  );
};
